<script setup lang="ts">
//#region imports
import { computed, nextTick, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { usePermissions } from '@/composables/usePermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useApi } from '@/api';
import { useTableSort } from '@/composables/useTableLS';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import type {
  GetToolsPromoMatchesLeaguesData,
  GetToolsPromoMatchesLeaguesResponse,
  GetToolsPromoMatchesMatchesData,
  GetToolsPromoMatchesMatchesResponse,
  GetToolsPromoMatchesTeamsData,
  GetToolsPromoMatchesTeamsResponse,
} from '@/types/openapi-ts';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
//#endregion

//#region const
const LS_PATH = 'LeagueMatches';

const fields: UiTableNewField<GetToolsPromoMatchesMatchesResponse['payload'][number]>[] = [
  {
    name: 'starts_at',
    width: 140,
    align: 'center',
    sortable: 'custom',
    format: 'date-time',
  },
  {
    name: 'match',
    align: 'left',
    computedValue: getMatchName,
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const router = useRouter();
const popupManager = usePopupManager();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const detectPermissions = useDetectPermissions();
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'starts_at',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<GetToolsPromoMatchesMatchesResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);

const leagueId = ref<number | string | null>(null);
const leagueInfo = ref<Partial<GetToolsPromoMatchesLeaguesResponse['payload'][number]>>({});
const teamsList = ref<GetToolsPromoMatchesTeamsResponse['payload']>([]);
//#endregion

//#region computed
const breadcrumbs = computed(() => {
  return [
    {
      title: t('tools.landingLeagues.leagues.land_leagues'),
      link: `/tools/landing-leagues?site_id=${siteId.value}`,
    },
    {
      title: leagueInfo.value.name,
    },
  ];
});

const siteId = computed(() => {
  return String(route.query.site_id ?? '');
});

const canEditLeague = computed(() => {
  return hasPermission('tools_promo_landings_edit');
});
//#endregion

//#region methods
async function initData() {
  leagueId.value = +route.params.id!;
  if (!leagueId.value) {
    router.push('/tools/landing-leagues');
  }
  await getLeagues();
  await getTeams();
  await getMatches();
}

function _query() {
  return {
    ...optionsSort.value,
    site_id: siteId.value,
    league_id: leagueId.value,
  } as GetToolsPromoMatchesMatchesData['query'];
}

async function getMatches() {
  if (!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  const options = _query();
  try {
    const { data: { payload } } = await api.getMatches(options);
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

async function getLeagues() {
  try {
    const { data: { payload } } = await api.getLeagues({ site_id: siteId.value, search: leagueId.value } as GetToolsPromoMatchesLeaguesData['query']);
    leagueInfo.value = payload[0]!;
  } catch (error) {
    console.log(error);
  }
}

async function getTeams() {
  try {
    const { data: { payload } } = await api.getTeams({ league_id: leagueId.value } as GetToolsPromoMatchesTeamsData['query']);
    teamsList.value = payload;
  } catch (error) {
    console.log(error);
  }
}

function getMatchName(row: GetToolsPromoMatchesMatchesResponse['payload'][number]) {
  const team1 = teamsList.value.find((team) => team.id === row.team_home_id);
  const team2 = teamsList.value.find((team) => team.id === row.team_away_id);
  return team1 && team2 ? `${team1.name} - ${team2.name}` : '-';
}

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    getMatches();
  });
};

function openMatchPopup(id = null) {
  popupManager.landingMatchPopup.open({
    data: {
      matchId: id,
      leagueId: leagueId.value,
      siteId: siteId.value,
      teamsList: teamsList.value,
    },
    listeners: {
      success: () => {
        getMatches();
      },
    },
  });
}

function handleDelete(row: GetToolsPromoMatchesMatchesResponse['payload'][number]) {
  const name = getMatchName(row);
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: `${t('tools.landingLeagues.leagues.delete')} ${name}`,
      message: `${t('tools.landingLeagues.leagues.delete')} ${name}`,
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('tools.landingLeagues.leagues.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteMatch(row.id.toString());
          await getMatches();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}
//#endregion

//#region hooks
onBeforeMount(async () => {
  detectPermissions.checkRoute('tools_promo_landings_view');
  if (detectPermissions.checkRequest('tools_promo_landings_view')) {
    await initData();
  }
});
//#endregion
</script>

<template>
  <div class="league-matches">
    <Breadcrumbs :items="breadcrumbs" />
    <UiToolbar>
      <template v-slot:title>
        {{ leagueInfo.name }}
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="canEditLeague"
          filled
          icon="plus"
          @click="openMatchPopup()"
        >
          {{ $t('tools.landingLeagues.leagues.add_match') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="tools.landingLeagues.matches"
      @sort="handleSort"
    >
      <template
        v-if="canEditLeague"
        v-slot:append
      >
        <ElTableColumn
          align="center"
          width="76"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="pen"
              :tooltip="$t('domainManagement.buttons.edit')"
              @click="openMatchPopup(scope.row.id)"
            />
            <ActionIcon
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('tools.landingLeagues.leagues.delete')"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
